import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  getProfileDetails,
  getReligions,
  getCastes,
  getMotherTongue,
  getStar,
  getZodiac,
  getCountries,
  getStates,
  getCities,
  requestProfileInterest,
  unlockProfile,
  getUnlockedProfiles,
  getUserSubscriptions,
  getRequestsSent
} from '../utils/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function ProfileCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');
  const currentUserId = localStorage.getItem('matrimonyUserId');
  const ENTREPRENEUR_OCCUPATION_ID = '68515ca576ce3b5d818b5b08';

  const [profile, setProfile] = useState(null);
  const [startupDetails, setStartupDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('description');
  const allTabs = ['description', 'bio', 'gallery', 'jathagam', 'startup'];

  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [motherTongues, setMotherTongues] = useState([]);
  const [stars, setStars] = useState([]);
  const [zodiacSigns, setZodiacSigns] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [requestAccepted, setRequestAccepted] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    async function fetchAll() {
      try {
        setLoading(true);

        // 1) Profile + check if they own a startup
        const profileRes = await getProfileDetails(id);
        const raw = profileRes.data ?? profileRes;
        const nested = raw.data ?? raw;
        const listing = nested.profile || nested;
        listing.isStartupOwner = Boolean(nested.startup);
        setProfile(listing);

        // 2) Parallel lookups
        const [
          rels, cst, mTongues, str, zSigns,
          couns, sts, cts,
          unlockedList, sentRequests, subscriptions
        ] = await Promise.all([
          getReligions(), getCastes(), getMotherTongue(),
          getStar(), getZodiac(),
          getCountries(), getStates(), getCities(listing.state),
          getUnlockedProfiles(token), getRequestsSent(token), getUserSubscriptions(token)
        ]);

        setReligions(rels);
        setCastes(cst);
        setMotherTongues(mTongues);
        setStars(str);
        setZodiacSigns(zSigns);
        setCountries(couns);
        setStates(sts);
        setCities(cts);

        setSubscribed((subscriptions || []).length > 0);

        // 3) Requests
        const sent = sentRequests.find(r => r.targetProfile === listing._id);
        if (sent) {
          setRequested(true);
          if (sent.status.toLowerCase() === 'accepted') {
            setRequestAccepted(true);
          }
        }

        // fix unlocking logic
        if (unlockedList.some(p => p.profile._id === listing._id)) {
          setUnlocked(true);
        }

        // 4) Startup details fetch
        if (nested.startup) {
          try {
            const sid = nested.startup._id || nested.startup;
            const startupRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/startups/${sid}`
            );
            setStartupDetails(startupRes.data.data ?? startupRes.data);
          } catch (err) {
            console.error('Startup load error:', err);
          }
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, [id, token]);

  // ensure `startup` tab only if details exist
  useEffect(() => {
    if (activeTab === 'startup' && !startupDetails) {
      setActiveTab('description');
    }
  }, [startupDetails, activeTab]);

  const handleUnlock = async () => {
    if (!subscribed) {
      toast.info('No profile limit remaining, subscribe to unlock', {
        onClose: () => navigate('/matrimony-pricing'),
      });
      return;
    }
    setUnlocking(true);
    try {
      await unlockProfile(id, token);
      setUnlocked(true);
      toast.success('Full details unlocked');
    } catch (err) {
      toast.error(err.message || 'Failed to unlock');
    } finally {
      setUnlocking(false);
    }
  };

  const handleRequest = async () => {
    if (requesting || requested) return;
    setRequesting(true);
    try {
      await requestProfileInterest(id, token);
      setRequested(true);
      toast.success('Request sent successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to send request');
    } finally {
      setRequesting(false);
    }
  };

  const lookup = (list, itemId) =>
    list.find(item => item._id === itemId)?.name || '—';

  if (loading) return <div className="py-8 text-center">Loading profile...</div>;
  if (error) return <div className="py-8 text-center text-red-500">{error}</div>;
  if (!profile) return <div className="py-8 text-center">Profile not found.</div>;

  const isPrivate = profile.visibility === 'Private';
  const isOwnProfile = profile.user === currentUserId;
  const availableTabs = allTabs.filter(t => t !== 'startup' || startupDetails);

  return (
    <>
      <ToastContainer />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row p-6 gap-20">
          <div className="flex flex-col items-center">
            <div className="relative">
              <img
                src={profile.images?.[0] || '/fallback.jpg'}
                alt={profile.fullName}
                className={`rounded-2xl w-72 h-96 object-cover object-top
                  ${isPrivate && !isOwnProfile && !unlocked ? 'filter blur-sm' : ''}`}
              />

              {isPrivate && !isOwnProfile && !unlocked && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <span className="text-white text-lg font-semibold">Private Profile</span>
                </div>
              )}

              {/* Entrepreneur badge */}
              {profile.occupation === ENTREPRENEUR_OCCUPATION_ID && (
                <span className="absolute top-2 right-2 bg-yellow-300 text-yellow-800 text-xs px-3 py-1 rounded-full">
                  Entrepreneur
                </span>
              )}
            </div>
          </div>

          {/* Details & actions */}
          <div className="flex-1">
            {isOwnProfile && (
              <div className="mb-4 text-blue-600 font-semibold">This is your own profile</div>
            )}
            {profile.lookingFor && (
              <div className="mb-2">
                <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                  ✅ Looking for a {profile.lookingFor}
                </span>
              </div>
            )}
            <h2 className="text-3xl font-bold mt-4 flex items-center gap-2">
              {profile.fullName}
            </h2>
            {profile.lastSeen && (
              <p className="text-sm text-gray-500 mt-1">
                🕒 Last seen {profile.lastSeen}
              </p>
            )}

            <div className="grid gap-2 mt-6 text-sm">
              <div className="flex">
                <span className="w-32 font-semibold">Date of Birth</span>:{' '}
                {profile.dob ? new Date(profile.dob).toLocaleDateString() : '—'}
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Height</span>: {profile.height || '—'}
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Marital Status</span>:{' '}
                {profile.maritalStatus || '—'}
              </div>
              <div className="flex">
                <span className="w-32 font-semibold">Religion</span>:{' '}
                {lookup(religions, profile.religion)}
              </div>
            </div>

            {!isOwnProfile && !requested && (
              <button
                onClick={handleRequest}
                disabled={requesting}
                className="mt-10 mr-3 px-12 py-2 bg-blue-600 text-white rounded-full"
              >
                {requesting ? 'Sending...' : 'Send Request'}
              </button>
            )}
            {!isOwnProfile && requested && !requestAccepted && (
              <span className="mt-4 inline-block text-yellow-600 font-semibold">
                Request sent
              </span>
            )}
            {!isOwnProfile && requestAccepted && !unlocked && (
              <button
                onClick={handleUnlock}
                disabled={unlocking}
                className="mt-4 px-12 py-2 bg-gradient-to-r from-[#E7603C] via-[#B9309A] to-[#B97639] text-white rounded-full"
              >
                {unlocking ? 'Unlocking...' : 'Unlock Full Details'}
              </button>
            )}
            {!isOwnProfile && unlocked && (
              <span className="mt-4 inline-block text-green-600 font-semibold">
                Profile Unlocked
              </span>
            )}
          </div>
        </div>

        {((requestAccepted && unlocked) || isOwnProfile) && (
          <div className="py-10 text-center">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-10 text-xl md:text-3xl font-semibold text-gray-600">
              {availableTabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`relative pb-2 px-3 ${activeTab === tab ? 'text-black' : 'hover:text-black'}`}
                >
                  <span className="capitalize">{tab}</span>
                  {activeTab === tab && (
                    <span className="absolute -bottom-1 left-0 h-1 w-full rounded bg-gradient-to-r from-[#E7603C] via-[#B9309A] to-[#B97639]" />
                  )}
                </button>
              ))}
            </div>
            {/* Tab Content */}
            <div className="mt-8 text-sm text-gray-600 space-y-4 px-4 md:px-20">
              {activeTab === 'description' && <div className="max-w-4xl mx-auto space-y-3 text-justify">{profile.description || 'No description provided.'}</div>}
              {activeTab === 'bio' && (
                <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                  <div className="flex"><span className="w-40 font-semibold">Age</span>: {profile.age || '—'}</div>
                  <div className="flex"><span className="w-40 font-semibold">Caste</span>: {lookup(castes, profile.caste)}</div>
                  <div className="flex"><span className="w-40 font-semibold">Mother Tongue</span>: {lookup(motherTongues, profile.motherTongue)}</div>
                  <div className="flex"><span className="w-40 font-semibold">Income</span>: {profile.annualIncome || '—'}</div>
                  <div className="flex"><span className="w-40 font-semibold">Star</span>: {lookup(stars, profile.star)}</div>
                  <div className="flex"><span className="w-40 font-semibold">Zodiac</span>: {lookup(zodiacSigns, profile.zodiacSign)}</div>
                  <div className="flex col-span-full"><span className="w-40 font-semibold">Address</span>: {profile.address || '—'}</div>
                  <div className="flex"><span className="w-40 font-semibold">Country</span>: {lookup(countries, profile.country)}</div>
                  <div className="flex"><span className="w-40 font-semibold">State</span>: {lookup(states, profile.state)}</div>
                  <div className="flex"><span className="w-40 font-semibold">City</span>: {lookup(cities, profile.city)}</div>
                </div>
              )}
              {activeTab === 'gallery' && (
                <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {profile.images?.length > 0 ? profile.images.map((img, i) => (
                    <img key={i} src={img} alt={`Gallery ${i + 1}`} className="rounded shadow" />
                  )) : <p className="col-span-full text-center">No images available.</p>}
                </div>
              )}
              {activeTab === 'jathagam' && (
                profile.jathagam?.length > 0 ? (
                  <ul className="list-disc pl-6 text-left inline-block text-base">
                    {profile.jathagam.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                ) : <p>No Jathagam information available.</p>
              )}

              {activeTab === 'startup' && startupDetails && (
                <div className="max-w-4xl mx-auto text-left space-y-4">
                  <h3 className="text-2xl font-bold">{startupDetails.startupName}</h3>
                  <p className="text-lg text-gray-600">{startupDetails.industry} Startup</p>
                  <div className="grid gap-4 mt-6">
                    <div className="flex"><span className="w-40 font-semibold">LinkedIn</span>: <a href={startupDetails.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600">Profile</a></div>
                    <div className="flex"><span className="w-40 font-semibold">Stage</span>: {startupDetails.stage}</div>
                    <div className="flex"><span className="w-40 font-semibold">Location</span>: {startupDetails.location}</div>
                    <div className="flex"><span className="w-40 font-semibold">Incubation</span>: {startupDetails.incubation}</div>
                    <div className="flex"><span className="w-40 font-semibold">Support Needed</span>: {startupDetails.support?.join(', ') || '—'}</div>
                  </div>

                  {startupDetails.pitchDeck && (
                    <div className="mt-6">
                      <a
                        href={startupDetails.pitchDeck}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        View Pitch Deck
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
